import { Link } from 'react-router-dom';
import { ArrowRight, Briefcase } from 'lucide-react';
import { PERMISSIONS } from '@/client/config/permissions';
import { useGate } from '@/client/hooks/useGate';
import { useHrEmployeesQuery, useLeaveRequestsQuery } from '@/client/api/hr/people';
import { useJobsQuery } from '@/client/api/hr/records';
import { useReviewCyclesQuery } from '@/client/api/hr/perf';
import { useAccountingOverviewQuery, useAssetsQuery, useComplianceQuery, usePurchaseOrdersQuery } from '@/client/api/business/spend';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { money, totals } from '../HumanResource/components/comp-format';

function Tile({ title, to, rows }: { title: string; to: string; rows: { label: string; value: string; warn?: boolean }[] }) {
  return (
    <Link to={to} className="group rounded-xl border border-gray-100 bg-white p-4 shadow-sm hover:border-gray-200">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
        <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-gray-500" />
      </div>
      <dl className="space-y-1.5">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center justify-between text-sm">
            <dt className="text-gray-500">{r.label}</dt>
            <dd className={r.warn ? 'font-medium text-amber-700' : 'font-medium text-gray-900'}>{r.value}</dd>
          </div>
        ))}
      </dl>
    </Link>
  );
}

const loading = (title: string, to: string) => <Tile title={title} to={to} rows={[{ label: 'Loading…', value: '' }]} />;

function PeopleTile() {
  const employees = useHrEmployeesQuery().data;
  const today = new Date().toISOString().slice(0, 10);
  const leave = useLeaveRequestsQuery({ from: today, to: today }).data;
  const pending = useLeaveRequestsQuery({ status: 'pending' }).data;
  if (!employees) return loading('People', '/admin/human-resource');
  return (
    <Tile
      title="People"
      to="/admin/human-resource/people/directory"
      rows={[
        { label: 'Active', value: String(employees.filter((e) => e.status === 'active').length) },
        { label: 'Out today', value: String((leave ?? []).filter((r) => r.status === 'approved').length) },
        { label: 'Time off waiting', value: String((pending ?? []).length), warn: (pending ?? []).length > 0 },
      ]}
    />
  );
}

function HiringTile() {
  const jobs = useJobsQuery().data;
  const cycles = useReviewCyclesQuery().data;
  if (!jobs) return loading('Hiring & reviews', '/admin/human-resource/recruiting/postings');
  const active = (cycles ?? []).find((c) => c.status === 'active');
  const inProgress = jobs.reduce((n, j) => n + (j.pipeline ? j.pipeline.applied + j.pipeline.screen + j.pipeline.interview + j.pipeline.offer : 0), 0);
  return (
    <Tile
      title="Hiring & reviews"
      to="/admin/human-resource/recruiting/postings"
      rows={[
        { label: 'Open roles', value: String(jobs.filter((j) => j.status === 'open').length) },
        { label: 'Candidates in progress', value: String(inProgress) },
        {
          label: active ? `${active.name} reviews` : 'Review cycle',
          value: active?.stats ? `${active.stats.self.submitted + active.stats.manager.submitted} / ${active.stats.self.total + active.stats.manager.total} in` : 'none active',
        },
      ]}
    />
  );
}

function MoneyTile() {
  const o = useAccountingOverviewQuery().data;
  if (!o) return loading('Money', '/admin/accounting');
  const month = o.pnl.rows.filter((r) => r.month === o.month && (r.totals.income || r.totals.costs));
  return (
    <Tile
      title="Money"
      to="/admin/accounting"
      rows={[
        { label: 'Profit this month', value: month.length ? month.map((r) => money(r.net, r.currency)).join(' · ') : '—' },
        { label: 'Bills owed', value: totals(o.owed) },
        { label: 'Overdue', value: totals(o.overdue), warn: o.overdue.length > 0 },
        { label: 'Payroll to approve or pay', value: String(o.payRunsOpen.length), warn: o.payRunsOpen.length > 0 },
      ]}
    />
  );
}

function OperationsTile() {
  const compliance = useComplianceQuery().data;
  const assets = useAssetsQuery().data;
  const pos = usePurchaseOrdersQuery().data;
  if (!compliance || !assets || !pos) return loading('Operations', '/admin/business/compliance');
  const renew = compliance.filter((c) => c.status === 'expiring_soon' || c.status === 'expired').length;
  return (
    <Tile
      title="Operations"
      to="/admin/business/compliance"
      rows={[
        { label: 'Compliance to renew', value: String(renew), warn: renew > 0 },
        { label: 'Equipment with people', value: `${assets.filter((a) => a.status === 'assigned').length} of ${assets.filter((a) => a.status !== 'retired').length}` },
        { label: 'Orders waiting for approval', value: String(pos.filter((p) => p.status === 'submitted').length), warn: pos.some((p) => p.status === 'submitted') },
      ]}
    />
  );
}

/** Business at a glance: one tile per area the viewer can see, each linking in. */
export default function BusinessOverviewPage() {
  useBreadcrumbs([{ label: 'Admin', href: '/admin' }, { label: 'Business' }]);
  const hr = useGate(PERMISSIONS.hr.view).allowed;
  const perf = useGate(PERMISSIONS.perf.view).allowed;
  const accounting = useGate(PERMISSIONS.accounting.view).allowed;
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mb-6">
        <div className="mb-1 flex items-center gap-3">
          <Briefcase className="h-7 w-7 text-slate-700" />
          <h1 className="text-2xl font-bold text-gray-900">Business</h1>
        </div>
        <p className="ml-10 max-w-2xl text-sm text-gray-500">People, money and operations at a glance. Each tile opens its area.</p>
      </div>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {hr && <PeopleTile />}
        {hr && perf && <HiringTile />}
        {accounting && <MoneyTile />}
        {accounting && <OperationsTile />}
      </div>
      {!hr && !accounting && <p className="text-sm text-gray-500">Nothing here for your roles yet. Your own record is in My workplace.</p>}
    </div>
  );
}
